// Utilities
import {trimValue} from '../../utilities/utilities';

// Components
import Avatar from './avatar';

const settings = {
    templates: {
        container: `<div class="rm-component audio-tooltip-player">
            <div class="audio-tooltip-player__slide">
                <div class="audio-tooltip-player__bar">
                    <div class="audio-tooltip-player__title"></div>
                    
                    <div class="audio-tooltip-player__playback-controls">
                        <button class="audio-tooltip-player__back-button"></button>
                        <button class="audio-tooltip-player__play-pause-button"></button>
                    </div>
                    
                    <div class="audio-tooltip-player__navigation-controls">
                        <button class="audio-tooltip-player__transcript-button">
                            <span>トランスクリプト</span>
                        </button>
                        <button class="audio-tooltip-player__close-button"></button>
                    </div>

                    <div class="audio-tooltip-player__progress"></div>
                </div>
                <div class="audio-tooltip-player__transcript">
                </div>
            </div>
        </div>`,

        scrollEndTrackingIndicator: `<span class="audio-tooltip__transcript-end"></span>`,
    },

    cssProperties: {
        playbackProgress: '--playback-progress',
    },

    classNames: {
        visible: 'audio-tooltip--visible',
        playing: 'audio-tooltip--playing',
        open: 'audio-tooltip--open',
    },

    selectors: {
        slide: '.audio-tooltip-player__slide',
        titleContainer: '.audio-tooltip-player__title',
        transcriptContainer: '.audio-tooltip-player__transcript',
        transcriptInnerContainer: '.audio-tooltip__transcript > div',
        backButton: '.audio-tooltip-player__back-button',
        playPauseButton: '.audio-tooltip-player__play-pause-button',
        transcriptButton: '.audio-tooltip-player__transcript-button',
        closeButton: '.audio-tooltip-player__close-button',
    },

    animation: {
        showBarDuration: 0.5,
        showBarDelay: 0.125,
        showAvatarDuration: 1.0,
        showAvatarDelay: 0.125,
        hideBarDuration: 0.375,
        hideAvatarDuration: 0.375,
        slowMoScale: 1 / 4,
    },
};

export default class Player {
    constructor(
        title,
        transcript,
        parameters,
        closeCallback,
    ) {
        /** @type {boolean} */
        this.isVisible = false;

        /** @type {boolean} */
        this.isOpen = false;

        // Create our player element
        /** @type {Element} */
        this.container = document.createElement('div');
        this.container.innerHTML = settings.templates.container;
        this.container = this.container.firstChild;

        // Find our DOM elements
        /** @type {Element} */
        this.slide = this.container
            .querySelector(settings.selectors.slide);

        /** @type {Element} */
        this.titleContainer = this.container
            .querySelector(settings.selectors.titleContainer);

        /** @type {Element} */
        this.transcriptContainer = this.container
            .querySelector(settings.selectors.transcriptContainer);

        /** @type {Element} */
        this.backButton = this.container
            .querySelector(settings.selectors.backButton);

        /** @type {Element} */
        this.playPauseButton = this.container
            .querySelector(settings.selectors.playPauseButton);

        /** @type {Element} */
        this.transcriptButton = this.container
            .querySelector(settings.selectors.transcriptButton);

        /** @type {Element} */
        this.closeButton = this.container
            .querySelector(settings.selectors.closeButton);

        // Create and add our avatar
        this.avatar = new Avatar();
        this.slide.appendChild(this.avatar.container);

        // Add title
        this.titleContainer.appendChild(title);

        // Add transcript content
        /** @type {Element} */
        this.transcriptScrollContainer = transcript;
        this.transcriptContainer.appendChild(this.transcriptScrollContainer);

        // Insert an element into the transcript content for use in
        // analytics tracking to determine when users scroll to the
        // end of the content here
        const transcriptInnerContainer = this.transcriptScrollContainer
            .querySelector(settings.selectors.transcriptInnerContainer);
        if (transcriptInnerContainer) {
            let scrollEndTrackingIndicator = document.createElement('div');
            scrollEndTrackingIndicator.innerHTML = settings
                .templates
                .scrollEndTrackingIndicator;
            scrollEndTrackingIndicator = scrollEndTrackingIndicator.firstChild;
            transcriptInnerContainer.appendChild(scrollEndTrackingIndicator);
        }

        // Setup our close button
        if (closeCallback) {
            this.closeButton.addEventListener('click', closeCallback);
        }

        // Setup our transcript button
        this.transcriptButton.addEventListener('click', () => {
            if (this.isOpen) {
                this.closeTranscript();
            } else {
                this.openTranscript();
            }
        });

        // Setup our play/pause button
        this.playPauseButton.addEventListener('click', () => {
            if (this.audioPlayer.paused || this.audioPlayer.ended) {
                // Rewind if we're at the end
                if (this.audioPlayer.ended) {
                    this.audioPlayer.currentTime = 0;
                }

                // Start playing
                this.audioPlayer.play();
            } else {
                // Pause
                this.audioPlayer.pause();
            }
        });

        // Setup back button
        this.backButton.addEventListener('click', () => {
            this.audioPlayer.currentTime = 0;

            // Update our progress in case we're not in playback,
            // when we're monitoring progress
            this.container.style.setProperty(
                settings.cssProperties.playbackProgress,
                0,
            );
        });

        // Add our element to the DOM
        document.body.appendChild(this.container);

        // Create audio player
        const sourceObjects = [];
        parameters
            .sources
            .forEach((sourceInfo) => {
                if (!sourceInfo.url || !sourceInfo.mimeType) {
                    throw new Error('Bad source definition for audio tooltip');
                }

                const source = document.createElement('source');
                source.src = sourceInfo.url;
                source.type = sourceInfo.mimeType;
                sourceObjects.push(source);
            });

        if (sourceObjects.length <= 0) {
            throw new Error('No audio sources specified for tooltip');
        }

        // Construct an <audio> element and append it to the DOM
        this.audioPlayer = document.createElement('audio');
        this.audioPlayer.preload = 'auto';
        this.audioPlayer.style.display = 'none';
        document.body.appendChild(this.audioPlayer);

        sourceObjects.forEach((source) => {
            this.audioPlayer.appendChild(source);
        });

        // Play & pause events
        this.audioPlayer.addEventListener('playing', () => {
            // Add our "playing" class
            this.container
                .classList
                .add(settings.classNames.playing);

            // Start monitoring the playback position - we're not using
            // the "timeupdate" event since it only ticks a couple times
            // per second, which doesn't look so hot
            const tick = () => {
                // Stop if we're no longer visible or playing
                if (this.audioPlayer.paused || this.audioPlayer.ended) {
                    // Remove our "playing" class
                    this.container
                        .classList
                        .remove(settings.classNames.playing);

                    // Make sure our playback progress reaches "1" at the end
                    if (this.audioPlayer.ended) {
                        this.container.style.setProperty(
                            settings.cssProperties.playbackProgress,
                            1,
                        );
                    }

                    return;
                }

                const percentComplete = trimValue(
                    this.audioPlayer.currentTime / this.audioPlayer.duration,
                    100000,
                );

                if (percentComplete < 0) {
                    percentComplete = 0;
                } else if (percentComplete > 1) {
                    percentComplete = 1;
                }

                this.container.style.setProperty(
                    settings.cssProperties.playbackProgress,
                    percentComplete,
                );

                // Request next animation frame
                window.requestAnimationFrame(tick);
            };

            tick();
        });
    }

    show(
        immediate = false,
        slowMo = false,
    ) {
        if (this.isVisible) {
            return;
        }

        this.isVisible = true;

        // Stop any ongoing animation
        if (this.showHideTimeline) {
            this.showHideTimeline.progress(1);
        }

        // Add our "visible" class
        this.container.classList.add(settings.classNames.visible);

        if (immediate) {
            // Stop here
            return;
        }

        // Animate transition
        this.showHideTimeline = gsap.timeline();
        if (slowMo) {
            this.showHideTimeline.timeScale(settings.animation.slowMoScale);
        }

        this.showHideTimeline.fromTo(
            this.slide,
            settings.animation.showBarDuration,
            {
                yPercent: 100,
            },
            {
                ease: 'power2.out',
                yPercent: 0,
            },
            settings.animation.showBarDelay,
        );

        this.avatar.createShowAnimation(
            this.container,
            this.showHideTimeline,
            settings.animation.showAvatarDuration,
            settings.animation.showAvatarDelay,
        );

        this.showHideTimeline.add(() => {
            this.showHideTimeline = null;

            // Clean up
            this.slide.removeAttribute('style');
        });

        // Rewind & play the audio
        this.audioPlayer.currentTime = 0;
        this.audioPlayer.play();
    }

    hide(
        immediate = false,
        slowMo = false,
    ) {
        if (!this.isVisible) {
            return;
        }

        this.isVisible = false;

        // Stop any ongoing animation
        if (this.showHideTimeline) {
            this.showHideTimeline.progress(1);
        }

        // Add our "visible" class (should already be present)
        this.container.classList.add(settings.classNames.visible);

        // Animate transition
        this.showHideTimeline = gsap.timeline();
        if (slowMo) {
            this.showHideTimeline.timeScale(settings.animation.slowMoScale);
        }

        this.showHideTimeline.fromTo(
            this.slide,
            settings.animation.hideBarDuration,
            {
                yPercent: 0,
            },
            {
                ease: 'power2.out',
                yPercent: 100,
            },
            0,
        );

        this.avatar.createHideAnimation(
            this.container,
            this.showHideTimeline,
            settings.animation.hideAvatarDuration,
            0,
            false,
            'power1.out',
        );

        this.showHideTimeline.add(() => {
            this.showHideTimeline = null;

            // Remove our "visible" class
            this.container.classList.remove(settings.classNames.visible);

            // Close the transcript if it's open
            this.closeTranscript();

            // Clean up
            this.slide.removeAttribute('style');
        });

        // Stop the audio
        this.audioPlayer.pause();
    }

    openTranscript() {
        if (this.isOpen) {
            return;
        }

        // Reset the scroll position on the transcript -
        // browsers tend to try and restore this
        // Also note that the "transcript" element is the
        // scroll container
        this.transcriptScrollContainer.scrollTo(0, 0);

        this.isOpen = true;
        this.container
            .classList
            .add(settings.classNames.open);
    }

    closeTranscript() {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;
        this.container
            .classList
            .remove(settings.classNames.open);
    }
}
